/**
 * AvatarUpload Component
 *
 * Reusable component for avatar upload functionality
 * Handles file selection, preview, and validation
 */

'use client';

import { toast } from 'sonner';
import { AVATAR_MAX_SIZE_BYTES, AVATAR_MAX_SIZE_MB } from '@hummii/shared-types';
import { Icon } from '@/src/shared/ui/icons/Icon';
import { Typography } from '@/src/shared/ui/typography/Typography';

export interface AvatarUploadProps {
  currentAvatarUrl?: string;
  selectedAvatar: File | null;
  onAvatarChange: (file: File | null) => void;
}

// Supported image types (must match backend ALLOWED_IMAGE_MIMES)
const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
];

export function AvatarUpload({
  currentAvatarUrl,
  selectedAvatar,
  onAvatarChange,
}: AvatarUploadProps) {
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type (strict check for supported formats)
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      toast.error('Unsupported image format. Please use JPEG, PNG, WebP, or GIF.');
      return;
    }

    // The API enforces the same limit; see AVATAR_MAX_SIZE_BYTES.
    if (file.size > AVATAR_MAX_SIZE_BYTES) {
      toast.error(`File size must be less than ${AVATAR_MAX_SIZE_MB}MB`);
      return;
    }

    onAvatarChange(file);
  };

  const previewUrl = selectedAvatar
    ? URL.createObjectURL(selectedAvatar)
    : currentAvatarUrl;

  return (
    <div className="relative inline-block group">
      <div className="w-[180px] h-[180px] rounded-full border-[6px] border-white bg-background-secondary overflow-hidden">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Profile"
            className="w-full h-full object-cover transition-opacity group-hover:opacity-90"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-background-tertiary">
            <Icon name="settings" size="xl" />
          </div>
        )}
      </div>

      {/* Camera Icon Button - Bottom Right */}
      <label
        className="absolute bottom-2 right-2 w-12 h-12 bg-accent-primary rounded-full flex items-center justify-center cursor-pointer shadow-lg hover:bg-accent-secondary transition-all hover:scale-110 active:scale-95"
        title={selectedAvatar ? 'Photo Selected' : 'Change Photo'}
      >
        <Icon name="camera" size="md" className="text-white" />
        <input
          type="file"
          accept=".jpg,.jpeg,.png,.webp,.gif"
          onChange={handleFileChange}
          className="hidden"
        />
      </label>
    </div>
  );
}
